export const MAX_RECENT_ITEMS = 5;

export const Status = {
  READY: "READY",
  GENERATING: "GENERATING",
  FAILED: "FAILED",
} as const;

export type Status = (typeof Status)[keyof typeof Status];

export const editTargets = [
  "手に持っているもの",
  "服",
  "ポーズ",
  "背景",
  "商品",
  "表情・視線",
  "画像内テキスト",
  "全体の雰囲気",
] as const;

export const formatOptions = [
  {
    label: "Instagram投稿",
    displaySize: "1080 × 1080",
    targetCanvasSize: "1080x1080",
    providerSize: "1024x1024",
    icon: "ig",
  },
  {
    label: "Instagramストーリー",
    displaySize: "1080 × 1920",
    targetCanvasSize: "1080x1920",
    providerSize: "1024x1536",
    icon: "ig",
  },
  {
    label: "Webバナー",
    displaySize: "1200 × 628",
    targetCanvasSize: "1200x628",
    providerSize: "1536x1024",
    icon: "web",
  },
  {
    label: "LINE配信用",
    displaySize: "1080 × 1080",
    targetCanvasSize: "1080x1080",
    providerSize: "1024x1024",
    icon: "line",
  },
] as const;

export type FormatOption = (typeof formatOptions)[number];

export const formats = formatOptions.map((format) => format.label);

export const tones = ["高級感", "ナチュラル", "ポップ", "信頼感", "セール訴求"] as const;

const templateImageFileNames = [
  "ChatGPT Image 2026年5月29日 23_30_17 (1).png",
  "ChatGPT Image 2026年5月29日 23_30_19 (2).png",
  "ChatGPT Image 2026年5月29日 23_30_19 (3).png",
  "ChatGPT Image 2026年5月29日 23_30_20 (4).png",
  "ChatGPT Image 2026年5月29日 23_30_21 (5).png",
  "ChatGPT Image 2026年5月29日 23_30_21 (6).png",
  "ChatGPT Image 2026年5月29日 23_30_22 (7).png",
  "ChatGPT Image 2026年5月29日 23_30_22 (8).png",
  "ChatGPT Image 2026年5月29日 23_30_23 (9).png",
  "ChatGPT Image 2026年5月29日 23_30_23 (10).png",
  "ダウンロード.jpeg",
  "2025-07-13-17.53.34.jpg",
] as const;

export const templateImages = templateImageFileNames.map((fileName, index) => ({
  src: `/front/assets/demo-img/${encodeURIComponent(fileName)}`,
  alt: `宣伝画像テンプレート ${index + 1}`,
}));

const talentImageFileNames = [
  "ambassador_img01_1@2x.jpg",
  "ambassador_img07_1@2x.jpg",
  "ambassador_img12_1@2x.jpg",
  "ambassador_img15_1@2x.jpg",
  "ambassador_img20_1@2x.jpg",
  "ambassador_img21_1@2x.jpg",
] as const;

export const talentImages = talentImageFileNames.map((fileName, index) => ({
  src: `/front/assets/talent-image/${encodeURIComponent(fileName)}`,
  alt: `所属タレント ${index + 1}`,
  priceLabel: "1ショット ¥100,000",
}));

export const imageGenerationModes = {
  standard: {
    label: "通常生成",
    model: "gpt-image-1-mini",
    quality: "auto",
  },
  highQuality: {
    label: "高品質生成",
    model: "gpt-image-1.5",
    quality: "high",
  },
} as const;

export type ImageGenerationMode = (typeof imageGenerationModes)[keyof typeof imageGenerationModes];

export interface Draft {
  brief: string;
  productName: string;
  appeal: string;
  copy: string;
  audience: string;
  instruction: string;
  brandStyle: string;
}

export interface CreativeVersion {
  id: string;
  number: number;
  image: string;
  model: string;
  mode: string;
  status: Status;
  target: string;
  instruction: string;
  createdAt: string;
}

export interface Creative {
  id: string;
  title: string;
  appeal: string;
  usage: string;
  format: string;
  tone: string;
  status: Status;
  createdAt: string;
  currentVersionId: string;
  versions: CreativeVersion[];
}

export interface DemoState {
  selectedTarget: string;
  selectedFormat: string;
  selectedTone: string;
  useHighQualityImageModel: boolean;
  isAdvancedSettingsOpen: boolean;
  isTemplatePanelOpen: boolean;
  isTalentPanelOpen: boolean;
  includeImageText: boolean;
  basePreviewUrl: string;
  baseImageDataUrl: string;
  referencePreviewUrls: string[];
  referenceImageDataUrls: string[];
  selectedCreativeId: string;
  selectedVersionId: string;
  dialogVersionId: string;
  isGenerating: boolean;
  isSuggestingCopy: boolean;
  generationError: string;
  copyAssistError: string;
  draft: Draft;
  creatives: Creative[];
}

export function formatOptionFor(label = ""): FormatOption {
  return formatOptions.find((format) => format.label === label) ?? formatOptions[0];
}

export function createInitialState(): DemoState {
  return {
    selectedTarget: editTargets[0] ?? "",
    selectedFormat: formats[0] ?? "",
    selectedTone: tones[0] ?? "",
    useHighQualityImageModel: false,
    isAdvancedSettingsOpen: false,
    isTemplatePanelOpen: false,
    isTalentPanelOpen: false,
    includeImageText: true,
    basePreviewUrl: "",
    baseImageDataUrl: "",
    referencePreviewUrls: [],
    referenceImageDataUrls: [],
    selectedCreativeId: "",
    selectedVersionId: "",
    dialogVersionId: "",
    isGenerating: false,
    isSuggestingCopy: false,
    generationError: "",
    copyAssistError: "",
    draft: {
      brief: "",
      productName: "",
      appeal: "",
      copy: "",
      audience: "",
      instruction: "",
      brandStyle: "",
    },
    creatives: [],
  };
}
